/**
 * Running cost calculator dialog.
 *
 * Opens from `[data-running-cost-open]`. The typed price is currency per kWh.
 * Hourly cost is price × watts ÷ 1000. Year and drying laundry multiply that
 * by 24 × 365. Savings always show the yearly difference between the dearest
 * rival and Meaco; drying laundry uses its own wattages.
 */
(() => {
  const TAG = 'running-cost-calculator';

  if (customElements.get(TAG)) return;

  const parseRate = (raw) => {
    const normalised = String(raw).trim().replace(',', '.');

    // Allow 0.25, .25, 0,25 and ,25. A bare "." is rejected.
    if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(normalised)) return null;

    const value = Number(normalised);

    if (!Number.isFinite(value) || value < 0) return null;

    return value;
  };

  const formatBracketMessage = (raw) => {
    const message = String(raw || '').trim().replace(/^per year\s+/i, '');

    if (!message) return '';

    return message.charAt(0).toUpperCase() + message.slice(1);
  };

  const matchBracket = (brackets, saving) => {
    if (!Number.isFinite(saving)) return null;

    const bands = (Array.isArray(brackets) ? brackets : [])
      .map((bracket) => ({
        min: Number(bracket.min),
        max: Number(bracket.max),
        message: bracket.message,
      }))
      .filter((bracket) => Number.isFinite(bracket.min) && Number.isFinite(bracket.max));

    // List order wins when bands overlap, so a shared edge keeps the earlier band.
    const containing = bands.find((bracket) => saving >= bracket.min && saving <= bracket.max);

    if (containing) return containing;

    // A saving above the top band, or in a gap between bands, uses the nearest lower band.
    return bands
      .filter((bracket) => bracket.min <= saving)
      .sort((left, right) => right.min - left.min)[0] || null;
  };

  class RunningCostCalculator extends HTMLElement {
    #dialog = null;
    #form = null;
    #growFrame = 0;

    connectedCallback() {
      this.#dialog = this.querySelector('dialog');
      this.#form = this.closest('product-form');
      this.#dialog?.addEventListener('close', this.#onDialogClose);
      this.#dialog?.addEventListener('click', this.#onBackdropClick);
      this.querySelector('[data-running-cost-close]')?.addEventListener('click', this.#onClose);
      this.querySelector('[data-running-cost-next]')?.addEventListener('click', this.#onNext);
      this.querySelector('[data-running-cost-back]')?.addEventListener('click', this.#onBack);
      this.querySelector('[data-running-cost-add]')?.addEventListener('click', this.#onAdd);
      this.#priceInput()?.addEventListener('keydown', this.#onPriceKeydown);
      this.#form?.addEventListener('variant:change', this.#onVariantChange);
      this.#syncAddButton();

      for (const tab of this.#tabs()) {
        tab.addEventListener('click', this.#onTabClick);
        tab.addEventListener('keydown', this.#onTabKeydown);
      }
    }

    disconnectedCallback() {
      this.#dialog?.removeEventListener('close', this.#onDialogClose);
      this.#dialog?.removeEventListener('click', this.#onBackdropClick);
      this.querySelector('[data-running-cost-close]')?.removeEventListener('click', this.#onClose);
      this.querySelector('[data-running-cost-next]')?.removeEventListener('click', this.#onNext);
      this.querySelector('[data-running-cost-back]')?.removeEventListener('click', this.#onBack);
      this.querySelector('[data-running-cost-add]')?.removeEventListener('click', this.#onAdd);
      this.#priceInput()?.removeEventListener('keydown', this.#onPriceKeydown);
      this.#form?.removeEventListener('variant:change', this.#onVariantChange);

      for (const tab of this.#tabs()) {
        tab.removeEventListener('click', this.#onTabClick);
        tab.removeEventListener('keydown', this.#onTabKeydown);
      }

      if (this.#growFrame) cancelAnimationFrame(this.#growFrame);
      if (this.#dialog?.open) this.#dialog.close();
    }

    open() {
      if (!(this.#dialog instanceof HTMLDialogElement) || this.#dialog.open) return;

      this.#showScreen('input');
      this.#setInvalid(false);
      this.#dialog.showModal();
      this.#priceInput()?.focus();
    }

    close() {
      if (!(this.#dialog instanceof HTMLDialogElement) || !this.#dialog.open) return;

      this.#dialog.close();
    }

    #onClose = () => {
      this.close();
    };

    #onBackdropClick = (event) => {
      if (event.target === this.#dialog) this.close();
    };

    #onDialogClose = () => {
      this.#showScreen('input');
      this.#setInvalid(false);
      delete this.dataset.rate;

      const firstTab = this.#tabs()[0];

      if (firstTab) this.#selectTab(firstTab);
    };

    #onPriceKeydown = (event) => {
      if (event.key !== 'Enter') return;

      event.preventDefault();
      this.#onNext(event);
    };

    #onNext = (event) => {
      event.preventDefault();

      const rate = parseRate(this.#priceInput()?.value || '');

      if (rate == null) {
        this.#setInvalid(true);
        this.#priceInput()?.focus();
        return;
      }

      this.dataset.rate = String(rate);
      this.#setInvalid(false);
      this.#showScreen('results');
      this.#applyCosts(this.#selectedTab());
      this.querySelector('[data-running-cost-results-heading]')?.focus();
    };

    #onBack = () => {
      this.#showScreen('input');
      this.#priceInput()?.focus();
    };

    #buyButton() {
      const button = this.#form?.querySelector('.buy-buttons__submit');

      return button instanceof HTMLButtonElement ? button : null;
    }

    #onVariantChange = () => {
      queueMicrotask(() => this.#syncAddButton());
    };

    #syncAddButton = () => {
      const add = this.querySelector('[data-running-cost-add]');
      const buy = this.#buyButton();

      if (!(add instanceof HTMLButtonElement)) return;

      if (!buy) {
        add.hidden = true;
        return;
      }

      add.hidden = false;

      const label = buy.querySelector('.button__label')?.textContent?.trim();
      const addLabel = add.querySelector('.button__label');

      if (label && addLabel) addLabel.textContent = label;

      add.disabled = buy.disabled;
      add.classList.toggle('button--disabled', buy.disabled);
      add.setAttribute('aria-disabled', buy.disabled ? 'true' : 'false');
    };

    #onAdd = () => {
      const buy = this.#buyButton();

      if (!buy || buy.disabled) return;

      this.close();
      buy.click();
    };

    #onTabClick = (event) => {
      const tab = event.currentTarget;

      if (tab instanceof HTMLButtonElement) this.#selectTab(tab);
    };

    #onTabKeydown = (event) => {
      const tabs = this.#tabs();
      const current = event.currentTarget;

      if (!(current instanceof HTMLButtonElement)) return;

      const index = tabs.indexOf(current);

      if (index < 0) return;

      let nextIndex = index;

      if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
      else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = tabs.length - 1;
      else return;

      event.preventDefault();
      this.#selectTab(tabs[nextIndex], true);
    };

    #selectTab(tab, focusTab = false) {
      if (!(tab instanceof HTMLButtonElement)) return;

      for (const candidate of this.#tabs()) {
        const selected = candidate === tab;

        candidate.setAttribute('aria-selected', selected ? 'true' : 'false');
        candidate.tabIndex = selected ? 0 : -1;
      }

      const panel = this.querySelector('[role="tabpanel"]');

      if (panel) panel.setAttribute('aria-labelledby', tab.id);

      const heading = this.querySelector('[data-running-cost-heading]');

      if (heading) heading.textContent = tab.dataset.heading || '';

      const hideFigure = tab.dataset.showFigure === 'false';

      for (const figure of this.querySelectorAll('[data-running-cost-figure]')) {
        figure.toggleAttribute('hidden', hideFigure);
      }

      this.#applyCosts(tab.dataset.runningCostTab);

      if (focusTab) tab.focus();
    }

    #selectedTab() {
      return this.#tabs().find((tab) => tab.getAttribute('aria-selected') === 'true')?.dataset.runningCostTab || 'year';
    }

    #config() {
      const node = this.querySelector('[data-running-cost-config]');

      if (!node) return {};

      try {
        return JSON.parse(node.textContent);
      } catch {
        return {};
      }
    }

    #watts(row, mode) {
      const raw = mode === 'laundry' ? row.dataset.laundry : row.dataset.power;

      if (raw == null || raw === '') return null;

      const value = Number(raw);

      if (!Number.isFinite(value) || value <= 0) return null;

      return value;
    }

    #cost(watts, rate, mode) {
      if (watts == null) return null;

      const hours = mode === 'hour' ? 1 : 24 * 365;

      return (rate * watts / 1000) * hours;
    }

    #formatMajor(major) {
      if (major == null) return '—';

      const config = this.#config();
      const cents = Math.round(major * 100);

      return this.#formatMoney(cents, config.moneyFormat);
    }

    #formatMoney(cents, format) {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents, format);
      }

      const amount = (cents / 100).toFixed(2);

      if (!format) return amount;

      return format
        .replace(/\{\{\s*amount\s*\}\}/, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, String(Math.round(cents / 100)));
    }

    #applyCosts(mode) {
      const rate = Number(this.dataset.rate);
      const rows = [...this.querySelectorAll('[data-running-cost-row]')];
      const costs = rows.map((row) => {
        if (!Number.isFinite(rate)) return null;

        return this.#cost(this.#watts(row, mode), rate, mode);
      });
      const priced = costs.filter((cost) => cost != null);
      const dearest = priced.length > 0 ? Math.max(...priced) : 0;
      const meacoIndex = rows.findIndex((row) => row.dataset.runningCostRow === 'meaco');
      const meacoCost = meacoIndex >= 0 ? costs[meacoIndex] : null;

      const shares = [];

      for (let index = 0; index < rows.length; index += 1) {
        const row = rows[index];
        const cost = costs[index];
        const amount = row.querySelector('[data-running-cost-amount]');
        const bar = row.querySelector('[data-running-cost-bar]');

        row.hidden = cost == null;

        if (amount) amount.textContent = this.#formatMajor(cost);

        if (bar) {
          const share = cost != null && dearest > 0 ? cost / dearest : 0;
          const currentRaw = bar.style.getPropertyValue('--running-cost-bar');
          const current = Number.parseFloat(currentRaw);

          // Year and hour use the same proportions. Leave an unchanged bar alone
          // so the width transition does not collapse and replay.
          if (currentRaw !== '' && Number.isFinite(current) && Math.abs(current - share) < 0.0001) continue;

          shares.push([bar, share]);
        }
      }

      this.#growBars(shares);

      const headline = this.querySelector('[data-running-cost-amount="headline"]');

      if (headline) headline.textContent = this.#formatMajor(meacoCost);

      const saving = this.querySelector('[data-running-cost-saving]');
      const savingAmount = this.querySelector('[data-running-cost-amount="save"]');
      const savingMode = mode === 'laundry' ? 'laundry' : 'year';
      const savingValue = this.#saving(rate, rows, meacoIndex, savingMode);
      const showSaving = savingValue != null && savingValue > 0;

      if (savingAmount) savingAmount.textContent = this.#formatMajor(showSaving ? savingValue : null);
      if (saving) saving.hidden = !showSaving;
      this.#applyMessage(showSaving ? savingValue : null);
    }

    #growBars(shares) {
      if (this.#growFrame) cancelAnimationFrame(this.#growFrame);

      const initial = [];

      for (const entry of shares) {
        const [bar, share] = entry;

        if (bar.style.getPropertyValue('--running-cost-bar') === '') {
          initial.push(entry);
        } else {
          bar.style.setProperty('--running-cost-bar', String(share));
        }
      }

      if (initial.length === 0) {
        this.#growFrame = 0;
        return;
      }

      // The stylesheet starts bars at 0. Wait until that width has painted,
      // then grow to the target. Later tab changes update the width directly.
      this.#growFrame = requestAnimationFrame(() => {
        this.#growFrame = requestAnimationFrame(() => {
          this.#growFrame = 0;

          for (const [bar, share] of initial) {
            bar.style.setProperty('--running-cost-bar', String(share));
          }
        });
      });
    }

    #saving(rate, rows, meacoIndex, mode) {
      const costs = rows.map((row) => this.#cost(this.#watts(row, mode), rate, mode));
      const meaco = meacoIndex >= 0 ? costs[meacoIndex] : null;
      const rivals = costs.filter((cost, index) => index !== meacoIndex && cost != null);

      if (meaco == null || rivals.length === 0) return null;

      return Math.max(...rivals) - meaco;
    }

    #applyMessage(yearlySaving) {
      const box = this.querySelector('[data-running-cost-message]');
      const text = this.querySelector('[data-running-cost-message-text]');

      if (!box || !text) return;

      const match = yearlySaving == null ? null : matchBracket(this.#config().brackets, yearlySaving);
      const message = match ? formatBracketMessage(match.message) : '';

      text.textContent = message;
      box.hidden = message === '';
      box.closest('[data-running-cost-saving]')?.classList.toggle('running-cost__save--with-message', message !== '');
    }

    #showScreen(name) {
      this.classList.toggle('running-cost--results', name === 'results');

      for (const screen of this.querySelectorAll('[data-running-cost-screen]')) {
        screen.hidden = screen.dataset.runningCostScreen !== name;
      }
    }

    #setInvalid(invalid) {
      const input = this.#priceInput();
      const error = this.querySelector('[data-running-cost-error]');

      if (input) input.setAttribute('aria-invalid', invalid ? 'true' : 'false');
      if (error) error.hidden = !invalid;
    }

    #priceInput() {
      return this.querySelector('[data-running-cost-price]');
    }

    #tabs() {
      return [...this.querySelectorAll('[data-running-cost-tab]')].filter((tab) => !tab.hidden);
    }
  }

  customElements.define(TAG, RunningCostCalculator);

  if (!window.__themeRunningCostTriggers) {
    window.__themeRunningCostTriggers = true;

    document.addEventListener('click', (event) => {
      const trigger = event.target instanceof Element
        ? event.target.closest('[data-running-cost-open]')
        : null;

      if (!(trigger instanceof HTMLElement)) return;

      event.preventDefault();

      const form = trigger.closest('product-form');
      const host = (form || document).querySelector(TAG);

      if (host && typeof host.open === 'function') host.open();
    });
  }
})();
